import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  writeBatch,
  increment,
  Timestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Vote } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

export const useVoting = (roomCode: string | null, roundId: string | null, allowSelfVote: boolean = true) => {
  const [hasVoted, setHasVoted] = useState(false);
  const [currentVote, setCurrentVote] = useState<string | null>(null);
  const [votesReceived, setVotesReceived] = useState(0);
  const [votedPlayers, setVotedPlayers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();

  // Check if current user has voted in this round
  useEffect(() => {
    if (!roomCode || !roundId || !user?.uid) {
      setHasVoted(false);
      setCurrentVote(null);
      return;
    }

    const voteRef = doc(db, 'rooms', roomCode, 'rounds', roundId, 'votes', user.uid);
    
    const unsubscribe = onSnapshot(
      voteRef,
      (doc) => {
        if (doc.exists()) {
          const vote = doc.data() as Vote;
          setHasVoted(true);
          setCurrentVote(vote.targetPlayerId);
        } else {
          setHasVoted(false);
          setCurrentVote(null);
        }
      },
      (error) => {
        console.error('Error listening to vote:', error);
      }
    );

    return unsubscribe;
  }, [roomCode, roundId, user]);

  // Listen to all votes in the current round
  useEffect(() => {
    if (!roomCode || !roundId) {
      setVotesReceived(0);
      setVotedPlayers(new Set());
      return;
    }

    const votesRef = collection(db, 'rooms', roomCode, 'rounds', roundId, 'votes');
    
    const unsubscribe = onSnapshot(
      votesRef,
      (snapshot) => {
        const votes = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Vote[];
        
        setVotesReceived(votes.length);
        setVotedPlayers(new Set(votes.map(vote => vote.voterId)));
      },
      (error) => {
        console.error('Error listening to votes:', error);
      }
    );

    return unsubscribe;
  }, [roomCode, roundId]);

  const submitVote = useCallback(async (targetPlayerId: string): Promise<void> => {
    if (!roomCode || !roundId || !user?.uid) {
      throw new Error('Datos de votación incompletos');
    }


    // Check for self-voting attempt (only block if allowSelfVote is false)
    if (user.uid === targetPlayerId && !allowSelfVote) {
      console.warn('⚠️ DEBUG: Self-vote attempt detected and blocked (allowSelfVote=false):', {
        voterId: user.uid,
        targetPlayerId,
        allowSelfVote
      });
      showError('No puedes votar por ti mismo');
      return;
    }

    // Log self-vote when allowed
    if (user.uid === targetPlayerId && allowSelfVote) {
      console.log('✅ DEBUG: Self-vote allowed:', {
        voterId: user.uid,
        targetPlayerId,
        allowSelfVote
      });
    }

    // If user already voted, prevent changing vote with user-friendly message
    if (hasVoted && currentVote !== targetPlayerId) {
      showError('Ya has votado. No puedes cambiar tu voto.');
      return;
    }
    
    // If trying to vote for the same person, just return silently
    if (hasVoted && currentVote === targetPlayerId) {
      return;
    }

    setLoading(true);
    try {
      const batch = writeBatch(db);

      // Create/update vote document
      const voteRef = doc(db, 'rooms', roomCode, 'rounds', roundId, 'votes', user.uid);
      const vote: Omit<Vote, 'id'> = {
        voterId: user.uid,
        targetPlayerId,
        roundId,
        submittedAt: Timestamp.now()
      };

      batch.set(voteRef, vote);

      // Update vote tally in round
      const roundRef = doc(db, 'rooms', roomCode, 'rounds', roundId);
      
      // Only for first-time votes (no need to handle changes since they're prevented above)
      batch.update(roundRef, {
        [`tally.${targetPlayerId}`]: increment(1)
      });

      await batch.commit();
      showSuccess('Voto registrado');
    } catch (error: any) {
      console.error('Error submitting vote:', error);
      
      // Check if it's a permission error (trying to change vote)
      if (error.code === 'permission-denied' && hasVoted) {
        console.log('🔒 Vote change blocked by security rules - this is expected behavior');
        showError('Ya has votado. No puedes cambiar tu voto.');
      } else {
        showError('Error al enviar el voto. Inténtalo de nuevo.');
      }
      
      // Don't throw error to prevent console spam
      return;
    } finally {
      setLoading(false);
    }
  }, [roomCode, roundId, user, currentVote, hasVoted, showError, showSuccess]);

  return {
    hasVoted,
    currentVote,
    votesReceived,
    votedPlayers,
    loading,
    submitVote
  };
};